import Vue from 'vue'
import App from '~src/App.vue'
import * as Sentry from '@sentry/vue'
import { i18n, setupLanguage } from './i18n'
import VueApollo from 'vue-apollo'
import { apolloClient } from '@/services/apollo'

import store from '~store/store'
import { router } from '~router/router'
import { sync } from 'vuex-router-sync'
import { installVuetify, createVuetifyInstance } from '~src/plugins/vuetify'

import '@mdi/font/css/materialdesignicons.css'
import './registerServiceWorker'

import 'vue2-animate/dist/vue2-animate.min.css'

import money from 'v-money'
import VueMoment from 'vue-moment'
import Toasted from 'vue-toasted'
import VueClipboard from 'vue-clipboard2'
import Print from 'vue-print-nb'
import VueShortKey from 'vue-shortkey'
import VueResize from 'vue-resize'
import 'vue-resize/dist/vue-resize.css'

import PhotoGallery from '~plugins/photoGallery/index'
import Confirm from '~plugins/confirm/confirm'
import TwoFa from '~plugins/twoFa/twoFa'
import DynamicDialog from '~plugins/dynamicDialog/dynamicDialog'
import UploadQueue from '~plugins/upload/uploadQueue'
import Loading from '~plugins/loading/loading'
import LoggedUser from '~plugins/loggedUser/loggedUser'
import EventBus from '~plugins/eventBus/eventBus'
import IsOnline from '~plugins/isOnline/isOnline'
import Whitelabel from '~plugins/whitelabel/whitelabel'
import UrlData from '~plugins/urlData/urlData'
import ClickOutside from '~plugins/clickOutside/ClickOutside'
import Comments from '~plugins/comments/comments'

import VueMask from 'v-mask'

import { createApiClientInstance } from '~config/http-api-config'
import { createNoAuthApiClientInstance } from '~config/no-auth-http-api-config'

import Filters from '~filters/filters'

import _ from 'lodash'

import '~src/styles/main.scss'
import '~src/styles/vuetify-overrides.scss'

import moment from 'moment'
// require('moment/locale/pt')
import 'moment/min/locales.min'

// Detect PhoneGap
Vue.isMobile = Vue.prototype.$isMobile = window.isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)

const setupDependencies = () => {
  installVuetify()
  Vue.use(VueResize)
  Vue.use(VueApollo)
  Vue.use(Whitelabel)
  Vue.use(UrlData)
  Vue.use(LoggedUser)
  Vue.use(EventBus)
  Vue.use(IsOnline)
  Vue.use(VueClipboard)
  Vue.use(money, { precision: 3 })
  Vue.use(VueMask)
  Vue.use(VueMoment, {
    moment
  })
  Vue.use(Confirm)
  Vue.use(TwoFa)
  Vue.use(DynamicDialog)
  Vue.use(UploadQueue)
  Vue.use(Toasted, {
    theme: 'toasted-primary',
    position: 'bottom-center',
    duration: 5000
  })
  Vue.use(Loading)
  Vue.use(Print)
  Vue.use(ClickOutside)
  Vue.use(Comments)
  Vue.use(VueShortKey)
  Vue.use(PhotoGallery)
}

const setupRoutes = () => {
  sync(store, router)

  // eslint-disable-next-line no-unused-vars
  router.beforeEach((to, from, next) => {
    const token = Vue.authToken

    if (to.path === from.path) {
      next()
      return
    }

    if (to.meta?.bypassToken === true) {
      next()
    } else if (!token && to.query.token) {
      store.dispatch('authViaToken', to.query.token).then(() => {
        next()
      })
    } else if (token) {
      if (!store.state.initialDataLoaded) {
        next({
          name: 'initialLoading',
          query: {
            next: to.fullPath
          }
        })
      } else if (to.meta?.superAdminOnly && !Vue.roleStaff) {
        next('/')
      } else if (to.meta?.access && Array.isArray(to.meta.access) && !Vue.hasAnyAccess(to.meta.access)) {
        // Verifica permissões - hasAnyAccess já libera para admin/staff
        next('/')
      } else {
        const showModal = to.matched.some(view => view.meta && view.meta.showModal)

        if (showModal) {
          if (from.name === 'initialLoading') {
            // In case of user refresh browser when in a modal, system will redirect to parent route or root route.
            if (to.matched.length > 2) {
              _.set(to, 'meta.closeRoute', to.matched[to.matched.length - 2].name)
              _.set(to, 'meta.closeRouteParams', to.params)
            }
          }
          next()
        } else {
          window.scrollTo(0, 0)
          next()
        }
      }
    } else {
      next({
        name: 'login',
        query: {
          next: to.fullPath
        }
      })
    }
  })

  // eslint-disable-next-line no-unused-vars
  router.afterEach(to => {
    const parentRoute = (to.path.split('/')[2] || '').split('?')[0]
    store.dispatch('setCurrentRoute', {
      name: to.name,
      path: to.path,
      fullPath: to.fullPath,
      params: to.params,
      query: to.query,
      parentRoute
    })
  })
}

const setupGlobalErrorHandlers = () => {
  Vue.config.errorHandler = event => {
    console.error(event)
  }

  window.onunhandledrejection = e => {
    console.warn('Unhandled Promise Rejection', e.reason)
  }
}

const requestPersistentStorage = () => {
  if (window.navigator.storage && window.navigator.storage.persist) {
    window.navigator.storage.persist()
  }
}

const setupUserLogin = () => {
  createNoAuthApiClientInstance()
  Vue.bus.$on('userLogin', () => {
    createApiClientInstance()

    // update Sentry user context
    const token = Vue.prototype.$authToken
    if (token) {
      Sentry.setUser({
        id: Vue.prototype.$loggedUser.id,
        name: Vue.prototype.$loggedUser.name,
        email: Vue.prototype.$loggedUser.email
      })

      Sentry.setContext('organization', {
        organizationId: Vue.prototype.$currentOrganization.id,
        organizationName: Vue.prototype.$currentOrganization.name
      })
    }

    if (window.location.href.match('/reports/')) {
      store.dispatch('setInitialDataLoaded', true)
    } else {
      store
        .dispatch('getInitialData')
        .catch(() => {
          Vue.toasted.error('Sessão expirada. Por favor, faça login novamente.')
        })
        .finally(() => {
          store.dispatch('setInitialDataLoaded', true)
        })
    }

    // update whitelabel options
    Vue.loadWhitelabel(Vue.currentOrganization.id)
    setupLanguage(Vue.getUserPreference('language'))
  })

  // Use cache to keep user logged
  return Vue.loadUserCache().then(token => {
    if (token) {
      store.dispatch('getMe')
      Vue.bus.$emit('userLogin')
    }
  })
}

export const feedback = Sentry.feedbackIntegration({
  autoInject: false,
  colorScheme: 'system',
  lang: 'pt-br',
  showEmail: false,
  showName: false,
  showBranding: false,
  useSentryUser: {
    name: 'name',
    email: 'email'
  },
  triggerLabel: 'Reportar erro',
  formTitle: 'Reportar erro',
  submitButtonLabel: 'Enviar',
  cancelButtonLabel: 'Cancelar',
  confirmButtonLabel: 'Confirmar',
  addScreenshotButtonLabel: 'Adicionar captura de tela',
  removeScreenshotButtonLabel: 'Remover captura de tela',
  nameLabel: 'Nome',
  namePlaceholder: 'Seu nome',
  emailLabel: 'E-mail',
  emailPlaceholder: 'Seu e-mail',
  isRequiredLabel: 'Obrigatório',
  messageLabel: 'Mensagem',
  messagePlaceholder: 'Descreva o que aconteceu',
  successMessageText: 'Obrigado por enviar seu feedback!'
})

const setupSentry = () => {
  Sentry.init({
    Vue,
    dsn: 'https://8e6dc609924421569cbe65a21b32990b@o4507901932339200.ingest.us.sentry.io/4507922705743872',
    integrations: [feedback]
  })
}

const setupWhitelabel = () => {
  return Vue.loadWhitelabel().then(whitelabel => {
    return whitelabel
  })
}

/**************************************************
                MAIN START HERE
***************************************************/

const apolloProvider = new VueApollo({
  defaultClient: apolloClient
})

setupDependencies()
setupLanguage()
setupSentry()
setupGlobalErrorHandlers()
requestPersistentStorage()
Filters.install(Vue)

setupWhitelabel().then(whitelabel => {
  const vuetify = createVuetifyInstance(whitelabel)
  setupUserLogin().then(() => {
    setupRoutes()
    /* eslint-disable no-new */
    new Vue({
      router,
      store,
      i18n,
      vuetify,
      apolloProvider,
      render: h => h(App)
    }).$mount('#app')
  })
})
